import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

/**
 * Install PWA button component
 * Shows installation button with modal instructions
 */
export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone
      || document.referrer.includes('android-app://');

    setIsStandalone(standalone);

    if (standalone) {
      console.log('[PWA] App already installed (standalone mode)');
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log('[PWA] Install prompt available');
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
      console.log('[PWA] App installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // If beforeinstallprompt available, use it
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`[PWA] User response: ${outcome}`);

      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      // Otherwise, show manual instructions modal
      setShowModal(true);
    }
  };

  // Don't render if already installed
  if (isStandalone) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleInstallClick}
        className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm hover:shadow-md"
        aria-label="Zainstaluj aplikację"
      >
        <Download className="w-4 h-4" />
        Zainstaluj aplikację
      </button>

      {/* Installation Instructions Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4">
              <Smartphone className="w-8 h-8 text-white" />
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Zainstaluj aplikację
            </h2>

            <p className="text-slate-600 mb-6">
              Dodaj PropertyCheck.PRO do ekranu głównego dla szybszego dostępu:
            </p>

            {/* Android Chrome */}
            <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-2">Android (Chrome/Edge)</h3>
              <ol className="text-sm text-emerald-800 space-y-1 list-decimal list-inside">
                <li>Naciśnij menu (⋮) w prawym górnym rogu</li>
                <li>Wybierz <strong>"Zainstaluj aplikację"</strong> lub <strong>"Dodaj do ekranu głównego"</strong></li>
                <li>Potwierdź instalację</li>
              </ol>
            </div>

            {/* iOS Safari */}
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">iOS (Safari)</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Naciśnij przycisk <strong>"Udostępnij"</strong> (kwadrat ze strzałką)</li>
                <li>Przewiń w dół i wybierz <strong>"Dodaj do ekranu początkowego"</strong></li>
                <li>Nazwij aplikację i naciśnij <strong>"Dodaj"</strong></li>
              </ol>
            </div>

            <button
              onClick={() => setShowModal(false)}
              className="w-full mt-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
            >
              Rozumiem
            </button>
          </div>
        </div>
      )}
    </>
  );
}
